import React, { useMemo } from "react";

import { Box, ButtonBase, Typography } from "@mui/material";

import {
  granite,
  grayMedium,
  moovsBlue,
  moovsBlueSelected,
  orange,
  pickUpVariantColors,
  purple,
} from "design-system/colors";
import MoreInfoPanel from "./MoreInfoPanel";
import { OpenIcon, PinFilledIcon } from "design-system/icons";
import { Airport, PickUpVariant } from "types";
import { useLaunchDarklyFlags } from "globals/hooks";
import RoundChip from "design-system/components/RoundChip";

function Line(props: { height: string }) {
  return (
    <Box
      bgcolor={grayMedium}
      width="1.5px"
      height={props.height}
      my={0.5}
      mr={0.75}
    />
  );
}

type TripTimelineStepProps = {
  stop: {
    location: string;
    airport: Airport;
    stopIndex: number;
    variant: "airport" | "address";
    pickUpVariant: PickUpVariant;
    customLocationName?: string | null;
    customGooglePlusCode?: string | null;
  };
  estimatedDropOffTime: string;
  isLastStop?: boolean;
  isQuote: boolean;
};

function TripTimelineStep(props: TripTimelineStepProps) {
  const { stop, isLastStop, estimatedDropOffTime, isQuote } = props;
  const { enableCustomLocation } = useLaunchDarklyFlags();

  //hooks
  const stopLabel = useMemo(() => {
    if (stop.stopIndex === 1) {
      return "Pick-up";
    }
    if (isLastStop) {
      return `Drop-off`;
    }

    return `Stop ${stop.stopIndex - 1}`;
  }, [isLastStop, stop.stopIndex]);

  const airportText =
    stop.variant === "airport" &&
    `${stop.airport.airportName} Airport (${stop.airport.iataCode})`;

  // todo: why is location ever null on return trips with airports. it should be empty string
  const [address, ...rest] = (stop.location || "").split(",");
  const city = rest.join(",");
  const showCustomLocation = !!enableCustomLocation && !!stop.customLocationName;
  const googleMapsLocation =
    enableCustomLocation && stop.customGooglePlusCode
      ? stop.customGooglePlusCode
      : stop.location;

  const handleCustomLocationClick = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        googleMapsLocation
      )}`,
      "_blank"
    );
  };

  return (
    <Box display="flex" flexDirection="row">
      {/* stepper */}
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box height="18px" width="18px" style={{ position: "relative" }}>
          <PinFilledIcon
            color={
              stop.stopIndex === 1 ? orange : isLastStop ? purple : granite
            }
            size="small"
            text={stop.stopIndex !== 1 && !isLastStop ? stop.stopIndex - 1 : ""}
          />
        </Box>
        {!isLastStop && <Line height="100%" />}
      </Box>

      {/* label */}
      <Box display="flex" flexDirection="column" ml={2} mt={-0.5} mb={5}>
        <Box>
          <Typography variant="overline" mr={1}>
            {stopLabel}
          </Typography>
          {!isQuote && (
            <>
              {stop.pickUpVariant === PickUpVariant.MeetGreet && (
                <RoundChip
                  label="MEET & GREET"
                  {...pickUpVariantColors[stop.pickUpVariant]}
                />
              )}
              {stop.pickUpVariant === PickUpVariant.Curbside && (
                <RoundChip
                  label="CURBSIDE"
                  {...pickUpVariantColors[stop.pickUpVariant]}
                />
              )}
            </>
          )}
        </Box>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          {airportText || address}
        </Typography>
        <Typography variant="body2" style={{ maxWidth: 200 }}>
          {city}
        </Typography>

        {showCustomLocation && (
          <Box mt={2} width="100%" maxWidth={420}>
            <ButtonBase
              onClick={handleCustomLocationClick}
              sx={{
                alignItems: "center",
                bgcolor: moovsBlueSelected,
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                textAlign: "left",
                width: "100%",
              }}
            >
              <Box display="flex" alignItems="center">
                <Box mr={1.5}>
                  <PinFilledIcon color={moovsBlue} size="small" />
                </Box>
                <Box>
                  <Typography variant="overline" color={moovsBlue}>
                    Point
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {stop.customLocationName}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" ml={2}>
                <OpenIcon color={moovsBlue} size="small" />
              </Box>
            </ButtonBase>
          </Box>
        )}

        {/* more info panel */}
        <MoreInfoPanel
          stop={stop}
          stopLabel={stopLabel}
          estimatedDropOffTime={estimatedDropOffTime}
        />
      </Box>
    </Box>
  );
}

export default TripTimelineStep;
